import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { brand } from '../../theme';
import { formatDateTime } from '../../utils/format';
import StatusChip from '../ui/StatusChip';
import EmptyState from '../ui/EmptyState';
import type { StatusHistoryEntry } from './types';

const timeOf = (value?: string | null) => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

/** The project's status changes, newest first, with who made them. */
const ActivityPanel: React.FC<{ history: StatusHistoryEntry[] }> = ({ history }) => {
  const entries = useMemo(
    () => history.filter((entry) => entry && entry.status).sort((a, b) => timeOf(b.timestamp) - timeOf(a.timestamp)),
    [history]
  );

  if (entries.length === 0) {
    return (
      <EmptyState
        compact
        icon={<HistoryOutlinedIcon />}
        title="No activity yet"
        description="Status changes on this project appear here."
      />
    );
  }

  return (
    <Box component="ol" aria-label="Project activity" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {entries.map((entry, index) => (
        <Box
          component="li"
          key={`${entry.timestamp}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            columnGap: 1.5,
            rowGap: 0.5,
            px: 2.5,
            py: 1.5,
            borderTop: index === 0 ? 'none' : `1px solid ${brand.border}`,
          }}
        >
          <StatusChip kind="project" status={entry.status} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {[entry.timestamp ? formatDateTime(entry.timestamp) : null, entry.userName ? `by ${entry.userName}` : null]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            {entry.comments && (
              <Typography variant="body2" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                {entry.comments}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ActivityPanel;
